/**
 * Pure helpers for the Freight Billing Audit page (unit-tested directly).
 */
import type { FreightAuditBucket, FreightAuditRow } from '../services/freightAudit';
import { toCsv } from '../utils/csv';

export const BUCKET_ORDER: FreightAuditBucket[] = [
  'keyed_on_invoice',
  'keyed_at_scan',
  'auto_quoted_at_scan',
  'preset_from_portal',
  'no_freight_unexplained',
  'no_freight_waived',
  'no_freight_collect_or_third_party',
  'no_freight_will_call',
  'no_freight_no_shipment',
  'no_freight_company_vehicle',
];

export const BUCKET_LABELS: Record<FreightAuditBucket, string> = {
  keyed_on_invoice: 'Keyed on the Sage invoice',
  keyed_at_scan: 'Keyed at scan',
  auto_quoted_at_scan: 'Auto-quoted at scan',
  preset_from_portal: 'Preset from the portal',
  no_freight_unexplained: 'No freight (unexplained)',
  no_freight_waived: 'No freight (waived in portal)',
  no_freight_collect_or_third_party: 'No freight (collect / 3rd party)',
  no_freight_will_call: 'No freight (will call)',
  no_freight_no_shipment: 'No freight (no shipment: consignment / stock billing)',
  no_freight_company_vehicle: 'No freight (ChemMasters truck)',
};

/** Buckets that mean "someone had to do it by hand after the scan". */
export const ATTENTION_BUCKETS: ReadonlySet<FreightAuditBucket> = new Set([
  'keyed_on_invoice',
  'no_freight_unexplained',
]);

/** Truckload filter as the page shows it; maps onto the API's optional boolean. */
export type TruckloadMode = 'all' | 'truckload' | 'not_truckload';

export const TRUCKLOAD_MODE_LABELS: Record<TruckloadMode, string> = {
  all: 'All carriers',
  truckload: 'Truckload only',
  not_truckload: 'Non-truckload only',
};

export function truckloadParam(mode: TruckloadMode): boolean | undefined {
  if (mode === 'truckload') return true;
  if (mode === 'not_truckload') return false;
  return undefined;
}

export function truckloadLabel(isTruckload: boolean): string {
  return isTruckload ? 'Truckload' : 'No';
}

export function money(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toFixed(2)}`;
}

export function isoDay(value: string | null | undefined): string {
  if (!value) return '—';
  return value.length > 10 ? value.slice(0, 10) : value;
}

export function defaultRange(today: Date = new Date()): { date_from: string; date_to: string } {
  const to = today.toISOString().slice(0, 10);
  const from = new Date(today.getTime() - 90 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  return { date_from: from, date_to: to };
}

const CSV_HEADER = [
  'sales_order_no',
  'invoice_no',
  'invoice_date',
  'invoice_last_edited',
  'customer_no',
  'customer_name',
  'customer_po',
  'carrier',
  'truckload',
  'scanned_at',
  'invoice_freight',
  'order_record_freight',
  'scan_billed_freight',
  'portal_preset',
  'bucket',
  'confidence',
  'basis',
];

export function rowsToCsv(rows: FreightAuditRow[]): string {
  return toCsv(
    CSV_HEADER,
    rows.map((r) => [
      r.sales_order_no,
      r.invoice_no,
      r.invoice_date,
      r.invoice_updated ?? '',
      r.customer_no ?? '',
      r.customer_name ?? '',
      r.customer_po_no ?? '',
      r.carrier ?? '',
      r.is_truckload ? 'yes' : 'no',
      r.scan_at ?? '',
      r.invoice_freight,
      r.sales_site?.actual_freight_cost ?? '',
      r.invoice_queue?.real_freight_amount ?? '',
      r.preset_amount ?? '',
      BUCKET_LABELS[r.bucket],
      r.confidence,
      r.basis,
    ]),
  );
}

export function sourceLabel(state: string): string {
  switch (state) {
    case 'ok': return 'connected';
    case 'not_granted': return 'read access not granted yet';
    case 'unreachable': return 'not reachable from this environment';
    case 'not_applicable': return 'not applicable';
    default: return state;
  }
}
